import { useCallback, useEffect, useRef, useState } from "react";
import { api, API } from "../../lib/api";
import { toast } from "sonner";
import { Upload, X, Paperclip, Download, Trash2, FileText, Image as ImgIcon, Eye } from "lucide-react";
import LoadingState from "../../components/ui/loading-state";
import { formatTanggal } from "../../lib/tanggal";

/**
 * AttachmentPanel — upload/list/download/delete dokumen untuk piutang/utang.
 * Props:
 *   module: "piutang" | "utang"
 *   objId: string (piutang/utang id)
 *   onClose: () => void
 */
export default function AttachmentPanel({ module, objId, objLabel, onClose }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {url, name}
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const fileRef = useRef(null);
  const token = localStorage.getItem("hris_token");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.get(`/finance/${module}/${objId}/attachments`);
      setItems(r.data.items || []);
    } catch (e) { setLoadError(e); toast.error("Gagal memuat file"); }
    finally { setLoading(false); }
  }, [module, objId]);
  useEffect(() => { load(); }, [load]);

  const canPreview = (mime) => (mime || "").startsWith("image/") || (mime || "") === "application/pdf";

  const openPreview = async (att) => {
    try {
      const res = await fetch(`${API}/finance/${module}/${objId}/attachments/${att.id}/preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Preview gagal");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      setPreview({ url, name: att.original_name });
    } catch { toast.error("Gagal preview file"); }
  };

  const closePreview = () => {
    if (preview?.url) window.URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) return toast.error("File terlalu besar. Max 20 MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/finance/${module}/${objId}/attach`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`File '${file.name}' terupload ✓`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal upload"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (att) => {
    try {
      const res = await fetch(`${API}/finance/${module}/${objId}/attachments/${att.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download gagal");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = att.original_name; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Gagal download"); }
  };

  const remove = async (att) => {
    if (!window.confirm(`Hapus file '${att.original_name}'?`)) return;
    try { await api.delete(`/finance/${module}/${objId}/attachments/${att.id}`); toast.success("Terhapus ✓"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  };

  const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;
  const isImg = (mime) => (mime || "").startsWith("image/");

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><Paperclip className="w-5 h-5 text-emerald-600" /> Dokumen Lampiran</h3>
            <p className="text-xs text-slate-500">{objLabel}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <div>
            <input ref={fileRef} type="file" onChange={e => upload(e.target.files?.[0])} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" className="hidden" data-testid="att-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="att-upload-btn" className="w-full h-11 rounded-lg border-2 border-dashed border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-medium text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
              <Upload className="w-4 h-4" /> {busy ? "Uploading..." : "Upload Foto / PDF / Dokumen"}
            </button>
            <p className="text-[11px] text-slate-500 mt-1.5">Max 20 MB · Format: JPG, PNG, PDF, DOC, XLS, CSV, TXT</p>
          </div>

          <div className="space-y-2">
            <LoadingState loading={loading} error={loadError} onRetry={load} kosong={items.length === 0} pesanKosong="Belum ada dokumen dilampirkan" varian="kartu" jumlahKartu={2} data-testid="loading-attachments">
              {items.map(att => (
                <AttachmentRow key={att.id} att={att} module={module} objId={objId} token={token}
                  canPreview={canPreview(att.mime_type)} isImg={isImg(att.mime_type)}
                  fmtSize={fmtSize} onOpenPreview={openPreview}
                  onDownload={download} onRemove={remove} />
              ))}
            </LoadingState>
          </div>
        </div>
        {preview && (
          <div className="fixed inset-0 z-[60] bg-black/85 grid place-items-center p-4" onClick={closePreview}>
            <div onClick={e => e.stopPropagation()} className="max-w-4xl max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between mb-2 text-white">
                <p className="text-sm font-medium truncate">{preview.name}</p>
                <button onClick={closePreview} data-testid="att-preview-close" className="w-8 h-8 rounded bg-white/10 hover:bg-white/20 grid place-items-center"><X className="w-4 h-4" /></button>
              </div>
              <img src={preview.url} alt={preview.name} className="max-w-full max-h-[80vh] object-contain rounded-lg bg-white" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AttachmentRow({ att, module, objId, token, canPreview, isImg, fmtSize, onOpenPreview, onDownload, onRemove }) {
  const [thumb, setThumb] = useState(null);
  useEffect(() => {
    if (!canPreview) return;
    let cancelled = false;
    let currentUrl = null;
    (async () => {
      try {
        const res = await fetch(`${API}/finance/${module}/${objId}/attachments/${att.id}/preview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const blob = await res.blob();
        if (cancelled) return;
        currentUrl = window.URL.createObjectURL(blob);
        setThumb(currentUrl);
      } catch { /* ignore thumb errors */ }
    })();
    return () => { cancelled = true; if (currentUrl) window.URL.revokeObjectURL(currentUrl); };
  }, [att.id, canPreview, module, objId, token]);

  return (
    <div className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50/50 flex items-center gap-3">
      {canPreview ? (
        <button onClick={() => onOpenPreview(att)} data-testid={`att-thumb-${att.id}`} className="w-14 h-14 rounded overflow-hidden bg-slate-100 grid place-items-center shrink-0 border border-slate-200 hover:border-emerald-400 relative group">
          {thumb ? (
            <img src={thumb} alt="" className="w-full h-full object-cover" />
          ) : (
            isImg ? <ImgIcon className="w-5 h-5 text-slate-400" /> : <FileText className="w-5 h-5 text-slate-400" />
          )}
          <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/40 grid place-items-center transition">
            <Eye className="w-4 h-4 text-white opacity-0 group-hover:opacity-100" />
          </div>
        </button>
      ) : (
        <div className="w-14 h-14 rounded bg-slate-100 grid place-items-center text-slate-500 shrink-0">
          <FileText className="w-5 h-5" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 truncate">{att.original_name}</p>
        <p className="text-[10px] text-slate-500">{fmtSize(att.size)} · Upload {att.uploaded_by_name} · {formatTanggal(att.uploaded_at)}</p>
      </div>
      {canPreview && (
        <button onClick={() => onOpenPreview(att)} data-testid={`att-preview-${att.id}`} title="Preview" className="w-8 h-8 rounded hover:bg-slate-100 text-slate-600 grid place-items-center"><Eye className="w-4 h-4" /></button>
      )}
      <button onClick={() => onDownload(att)} data-testid={`att-dl-${att.id}`} className="w-8 h-8 rounded hover:bg-slate-100 text-slate-600 grid place-items-center"><Download className="w-4 h-4" /></button>
      <button onClick={() => onRemove(att)} data-testid={`att-del-${att.id}`} className="w-8 h-8 rounded hover:bg-rose-50 text-rose-600 grid place-items-center"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}
